/* eslint-disable */
import { SpotlightBannerCardProps } from '@mtnkente/cl-super-app-widgets';
import { sendJsonToNative } from '../app/hooks/UseWebViewMessageListener';
import { AppSpecificConfigs, Feed } from '../types/read-feed.types';
import { BannerResponse, CarouselSlide } from '../types/hero-banner.types';

export function timeSince(dateString: string): string {
  const now = new Date();
  const past = new Date(dateString);
  const diffMs = now.getTime() - past.getTime();

  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (years > 0) return `${years} year${years > 1 ? 's' : ''} ago`;
  if (months > 0) return `${months} month${months > 1 ? 's' : ''} ago`;
  if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;

  return 'Just now';
}

export function cleanFormattedText(formattedText: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(formattedText, 'text/html');

  // Remove all <svg> elements
  doc.querySelectorAll('svg').forEach((svg) => svg.remove());

  // Remove all <button> elements
  doc.querySelectorAll('button').forEach((button) => button.remove());

  // Return the cleaned HTML string
  return doc.body.innerHTML;
}

export function callNavigation(
  path: string,
  url?: string,
  options?: Record<string, unknown>
): void {
  try {
    sendJsonToNative(
      JSON.stringify({
        action: 'navigate',
        params: {
          pathname: path,
          uri: url,
          options,
        },
      })
    );
  } catch (e) {
    alert(e);
  }
}

export function triggerLogin(): void {
  sendJsonToNative(JSON.stringify({ action: 'Open_Login' }));
}

export const runningInBrowser = typeof window !== 'undefined';

export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

export function readConfigsFromLocalStorage(): AppSpecificConfigs | undefined {
  const configs = localStorage.getItem('configs');
  if (configs) {
    try {
      return JSON.parse(configs) as AppSpecificConfigs;
    } catch (e) {
      console.error('Error parsing configs from localStorage:', e);
    }
  }
  return;
}

export function transformContentDetails(contentDetails: any): Feed {
  return {
    id: contentDetails?.id,
    title: contentDetails?.title,
    content: contentDetails?.content,
    publishedAt: contentDetails?.publishedAt,
    lastUpdated: contentDetails?.lastUpdated,
    channel: contentDetails?.channel,
    coverImage: contentDetails?.coverImage?.url,
    isChannel: !!contentDetails?.isChannel,
    dateCreated: contentDetails?.dateCreated,
    userUpdated: contentDetails?.userUpdated,
    channelImageUri: contentDetails?.source?.url ?? '',
    channelName: contentDetails?.source?.name ?? '',
    formattedText: contentDetails?.formattedText,
    screen: contentDetails?.screen,
    isSponsored: contentDetails?.isSponsored,
    sponsoredBy: contentDetails?.sponsoredBy,
    isRecommendation: contentDetails?.isRecommendation,
  };
}

export const readStorage = (): Feed | undefined => {
  const storedData = localStorage.getItem('content_details');
  if (storedData) {
    try {
      const parsedData = JSON.parse(storedData);
      if (
        !parsedData.id ||
        !parsedData.coverImage ||
        !parsedData.title ||
        !parsedData.description
      ) {
        console.warn('Incomplete content details found in localStorage, returning undefined');
      } else {
        return {
          id: parsedData?.id,
          title: parsedData?.title,
          channelImageUri: parsedData?.source?.imageUri ?? '',
          formattedText: parsedData?.content,
          content: parsedData?.content,
          channel: parsedData?.source,
          channelName: parsedData?.source?.name ?? '',
          coverImage: parsedData?.coverImage?.url,
          description: parsedData?.description,
          source: parsedData?.source,
          publishedAt: parsedData?.publishedAt,
          type: parsedData?.type,
          isChannel: parsedData?.isChannel,
          screen: parsedData?.screen,
          dateCreated: parsedData?.dateCreated,
          userUpdated: parsedData?.userUpdated,
          isSponsored: parsedData?.isSponsored,
          sponsoredBy: parsedData?.sponsoredBy,
          isRecommendation: parsedData?.isRecommendation,
        };
      }
    } catch (error) {
      console.error('Error parsing content details from localStorage:', error);
      return undefined;
    }
  }
};

export const detectEnv = (ua: string) => {
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const androidWV = isAndroid && (/;\s*wv\)/i.test(ua) || /Version\/\d+/i.test(ua));
  const iosWV =
    isIOS &&
    /AppleWebKit/i.test(ua) &&
    /Mobile\//i.test(ua) &&
    !/Safari|CriOS|FxiOS|EdgiOS/i.test(ua);
  return androidWV || iosWV ? 'webview' : 'browser';
};

export const isURL = (imageURL?: string): boolean => {
  if (!imageURL) {
    return false;
  }
  try {
    new URL(imageURL);
    return true;
  } catch {
    return false;
  }
};

export const buildUrl = (image?: string): string => {
  if (!image || image.trim() === '') return '';

  if (isURL(image)) return image;

  const baseUrl = process.env.PUBLIC_ASSETS_URL;
  const hasFormatParam = image.includes('?format=webp');

  return `${baseUrl}${image}${hasFormatParam ? '' : '?format=webp'}`;
};

export const htmlToPlainText = (html: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

// Helper function to safely parse timestamp
export const parseTimestamp = (timestamp: string | number | undefined): number => {
  if (typeof timestamp === 'number') {
    return timestamp;
  }
  if (typeof timestamp === 'string') {
    const parsed = new Date(timestamp).getTime();
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

export function parseBannerResponse(response: BannerResponse): {
  isHidden: boolean;
  banners: SpotlightBannerCardProps[];
} {
  const carouselSlides = response?.slides;

  return {
    isHidden: response?.isHidden ?? false,
    banners: carouselSlides?.map((slide: CarouselSlide) => {
      return {
        $title: slide?.title,
        $description: slide?.description,
        $backgroundUrl: buildUrl(slide?.backgroundImageUrl),
        $logoUrl: buildUrl(slide?.channelImageUrl),
        salesTapeProps: slide?.tagline
          ? {
              $label: slide?.tagline,
              $orientation: 'top-left',
              $variant: 'brand',
            }
          : undefined,
      };
    }),
  };
}
